import React, { useEffect, useRef, useState, useCallback } from "react";
import MapComponent from "../../components/MapComponent/MapComponent";
import SearchBoxComponent from "../../components/SearchBoxComponent/SearchBoxComponent";
import { Img, StyledDiv, StyledMapButton } from "./styled";
import { Container, Nav, Navbar } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { useViewer } from "../../viewerContext";
import { FeatureCollection, Geometry, GeoJsonProperties } from "geojson";
import "../../assets/css/Loader.css";
import "../../assets/css/Viewer.css";
/*** SVGs  ***/
import logo from "../../assets/svg/ViewerLogo.svg";
import Lidar from "../../assets/svg/ViewLidar.svg";
import Camera360 from "../../assets/svg/ViewCamera360.svg";
import LidarActive from "../../assets/svg/ViewLidarActive.svg";
import Camera360Active from "../../assets/svg/ViewCamera360Active.svg";
import closeIcon from "../../assets/svg/close.svg";
import ViewerBar from "../../components/ViewerBar/ViewerBar";
import { DateTime } from "luxon";
import { IIndexedScanSession } from "../../api/Interfaces";
import axios from "axios";
import { Background } from "../public/styled";
import PanoramaViewer from "../../components/PanoramaComponent/PanoramaComponent";
import PointCloudViewer from "../../components/PointCloudComponent/PointCloudComponent";
import { Timeout } from "../public/Login";
import {
  Square3Stack3DIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/24/outline";
import { SensorType } from "../../api/Enums";
import { IUser } from "../../api/Interfaces";

/*** SVGs end ***/

// Function to iterate over the data and create a JSON object
const processViewerData = (
  data: FeatureCollection<Geometry, GeoJsonProperties>
) => {
  const features = data?.features || [];
  const processedData: {
    [key: string]: { [key: number]: { meter: number; index: number }[] };
  } = {};

  features.forEach((item: any, index: number) => {
    const attributes = item.properties.attributes;
    if (attributes) {
      const bandel: string = attributes[6];
      const kilometer: number = Math.round(attributes[4]);
      const meter: number = Math.round(
        parseFloat(attributes[5].replace("+", ""))
      );

      if (!processedData[bandel]) {
        processedData[bandel] = {};
      }
      if (!processedData[bandel][kilometer]) {
        processedData[bandel][kilometer] = [];
      }
      processedData[bandel][kilometer].push({ meter, index });
    }
  });

  return processedData;
};

export default function MainViewer() {
  /***Panorama ***/
  const location: any = useLocation();

  const [error, setError] = useState<any>(null);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const { viewerState, setViewerState } = useViewer();
  const [pointCloudCount, setPointCloudCount] = useState<number>();
  const [processedData, setProcessedData] = useState({});
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);

  const scanSessionObject: IIndexedScanSession = location.state.scanSession;
  const sensorScanId = scanSessionObject.sensor_scans.find(
    (scan) => scan.type === SensorType.panoramic_camera
  )?.id;
  
  // Determine initial active view based on user permissions and available sensor scans
  const getInitialActiveView = () => {
    if (currentUser?.has_image_access && sensorScanId) {
      return SensorType.panoramic_camera;
    } else if (currentUser?.has_pointcloud_access && pointCloudCount) {
      return SensorType.lidar;
    }
    // Fallback to old behavior if user data not loaded yet
    return sensorScanId ? SensorType.panoramic_camera : SensorType.lidar;
  };
  
  const [activeView, setActiveView] = useState<SensorType>(getInitialActiveView());
  const scanSessionDate: string = DateTime.fromISO(
    scanSessionObject.date.toString()
  ).toFormat("dd LLLL yyyy");
  const scanSessionFolder: string = DateTime.fromISO(
    scanSessionObject.date.toString()
  ).toFormat("yyyyLLdd_HHmmss");

  const scanSessionCRS: string = location.state.crs;
  const toSensorType: string = location.state.toSensorType;
  const potreeChildRef = useRef<any>();

  // const [hasLidar, setHasLidar] = useState<boolean>();
  // const [has360Camera, setHas360Camera] = useState<boolean>();
  // const [hasCamera, setHasCamera] = useState<boolean>();

  const [mapCSS, setMapCSS] = useState({
    width: "260px",
    height: "150px",
    left: "50px",
    bottom: "50px",
  });
  const [mapButtonCSS, setMapButtonCSS] = useState({
    width: "25",
    left: "60px",
    bottom: "60px",
  });
  const [expandedMap, setExpandedMap] = useState<boolean>(false);

  // New state variable to keep track of whether the map is satellite or schematic
  const [isSatellite, setIsSatellite] = useState(
    localStorage.getItem("MapStyle") === "mapbox://styles/mapbox/satellite-v9"
  );

  // Function to switch map style and save in localStorage
  const toggleMapStyle = () => {
    const newStyle = isSatellite
      ? "mapbox://styles/mapbox/streets-v11"
      : "mapbox://styles/mapbox/satellite-v9";
    setIsSatellite(!isSatellite);
    localStorage.setItem("MapStyle", newStyle); // save to localStorage
  };

  const handleSwitchPC2Pano = async (updatedPointCloudLoc: any) => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/scansessions/${scanSessionObject.id}/${scanSessionCRS}?center=${updatedPointCloudLoc.x}&center=${updatedPointCloudLoc.y}&to_sensor_type=${toSensorType}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        const newCameraFrame = response.data;

        if (newCameraFrame[0] !== viewerState.dataId + 1) {
          setViewerState({
            type: "UPDATE_FROM_POINTCLOUD",
            payload: { id: newCameraFrame[0] },
          });
        }
      });
  };

  const fetchData = useCallback(async function (id: number) {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/scansessions/${id}`, {
      headers: {
        "Content-Type": "application/json",
        "sec-fetch-mode": "no-cors",
      },
      method: "GET",
      credentials: "include",
      signal: Timeout(120).signal,
    });
    const respData = await response.json();
    const processedData = processViewerData(respData);
    setPointCloudCount(respData.point_cloud_count);
    delete respData.point_cloud_count;
    if (scanSessionObject.id === 1) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 588 },
      });
    } else if (scanSessionObject.id === 4) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 604 },
      });
    } else if (scanSessionObject.id === 6) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 12054 },
      });
    } else if (scanSessionObject.id === 7) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 5246 },
      });
    } else if (scanSessionObject.id === 9) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 9927 },
      });
    } else if (scanSessionObject.id === 17) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 4256 },
      });
    } else if (scanSessionObject.id === 20) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 3028 },
      });
    } else if (scanSessionObject.id === 21) {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: 68 },
      });
    } else {
      setViewerState({
        type: "INIT",
        payload: { data: respData, override_start_id: null },
      });
    }
    setProcessedData(processedData);
    setIsDataReady(true);
  }, [scanSessionObject.id, setViewerState]);

  useEffect(() => {
    fetchData(scanSessionObject.id).catch((error) => {
      setError(error);
    });
  }, [fetchData, scanSessionObject.id]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          signal: Timeout(7).signal,
        });
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
          
          // Update activeView based on user permissions
          if (userData.has_image_access && sensorScanId) {
            setActiveView(SensorType.panoramic_camera);
          } else if (userData.has_pointcloud_access && pointCloudCount) {
            setActiveView(SensorType.lidar);
          } else if (!userData.has_image_access && !userData.has_pointcloud_access) {
            // TODO: Handle case where user has no access to any data type
            console.warn('User has no access to image or pointcloud data');
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };
    fetchUser();
  }, [sensorScanId, pointCloudCount]);

  const SwitchNav = () => {
    const hasImageAccess = currentUser?.has_image_access ?? true;
    const hasPointcloudAccess = currentUser?.has_pointcloud_access ?? true;
    
    return (
      <Navbar className="switch-nav" variant="dark">
        <Container>
          <Nav className="me-auto">
            {hasImageAccess && sensorScanId && (
              <StyledDiv
                onClick={(e) => {
                  setActiveView(SensorType.panoramic_camera);
                  if (activeView === SensorType.lidar) {
                    handleSwitchPC2Pano(potreeChildRef.current.getAlert());
                  }
                }}
                css="float: right;
                                flex: 50%;
                                padding: 10px 10px 10px 0px;
                                width: auto;"
              >
                {activeView === SensorType.panoramic_camera && <Img src={Camera360Active} />}
                {activeView !== SensorType.panoramic_camera && <Img src={Camera360} />}
              </StyledDiv>
            )}
            {hasPointcloudAccess && (
              <StyledDiv
                onClick={(e) => {
                  setActiveView(SensorType.lidar);
                }}
                css="float: right;
                                flex: 50%;
                                padding: 10px 10px 10px 10px;
                                width: auto;"
              >
                {activeView === SensorType.lidar && <Img src={LidarActive} />}
                {activeView !== SensorType.lidar && <Img src={Lidar} />}
              </StyledDiv>
            )}
            <StyledDiv
              css="float: right;
                              flex: 50%;
                              padding: 10px 10px 10px 10px;
                              width: auto;"
            >
              <Link to="/">
                <Img src={closeIcon} />
              </Link>
            </StyledDiv>
          </Nav>
        </Container>
      </Navbar>
    );
  };

  const ViewerNav = () => {
    return (
      <StyledDiv
        css="position: absolute;
                            display: flex;
                            justify-content: space-between;
                            padding-right: 15%;
                            min-width: 100%;
                        "
      >
        <Img css="padding: 10px; z-index:1;" src={logo} />
        <ViewerBar ssd={scanSessionDate} />
        <SwitchNav />
      </StyledDiv>
    );
  };

  const expandMap = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.preventDefault();
    setExpandedMap(true);
    setMapCSS({ width: "25%", height: "60vh", left: "50px", bottom: "50px" });
    setMapButtonCSS({ width: "25", left: "60px", bottom: "60px" });
  };

  const collapseMap = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.preventDefault();
    setExpandedMap(false);
    setMapCSS({
      width: "260px",
      height: "150px",
      left: "50px",
      bottom: "50px",
    });
    setMapButtonCSS({ width: "25", left: "60px", bottom: "60px" });
  };

  const MiniMapBox = () => {
    return (
      <StyledDiv
        css={`
          background-color: green;
          background: rgba(135, 206, 235, 0.2);
          position: fixed;
          width: ${mapCSS.width};
          left: ${mapCSS.left};
          bottom: ${mapCSS.bottom};
          z-index: 2;
          border: solid #a9a9a9 1px;
          border-radius: 15px;
          overflow: hidden;
        `}
      >
        {expandedMap && ( // This means that the search function is only displayed when the MiniMapBox map is expended/large
          <SearchBoxComponent availableBandels={processedData} />
        )}
        <MapComponent
          mapStyle={{ height: mapCSS.height }}
          style={
            isSatellite
              ? "mapbox://styles/mapbox/satellite-v9"
              : "mapbox://styles/mapbox/streets-v11"
          }
        />
      </StyledDiv>
    );
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  } else {
    if (isDataReady) {
      return (
        <>
          <MiniMapBox />
          <ViewerNav />

          {/* Button to toggle map style */}
          <StyledMapButton
            css={`
              background: rgba(255, 255, 255, 0.9);
              border: 1px solid #ccc;
              border-radius: 4px;
              padding: 1px;
              position: fixed;
              left: calc(${mapButtonCSS.left} + 40px);
              bottom: ${mapButtonCSS.bottom};
              z-index: 2;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
            onClick={toggleMapStyle}
          >
            <Square3Stack3DIcon width="25" color="black" />
          </StyledMapButton>

          {/* Button to expand/minimize the map */}
          <StyledMapButton
            css={`
              background: rgba(255, 255, 255, 0.9);
              border: 1px solid #ccc;
              border-radius: 4px;
              padding: 1px;
              position: fixed;
              left: ${mapButtonCSS.left};
              bottom: ${mapButtonCSS.bottom};
              z-index: 2;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            {expandedMap ? (
              <ArrowsPointingInIcon
                width="25"
                color="black"
                onClick={collapseMap}
                style={{ cursor: "pointer" }}
              />
            ) : (
              <ArrowsPointingOutIcon
                width="25"
                color="black"
                onClick={expandMap}
                style={{ cursor: "pointer" }}
              />
            )}
          </StyledMapButton>
          {(currentUser?.has_image_access ?? true) && (sensorScanId !== undefined) && (<div className="viewer-panorama">
            {activeView === SensorType.panoramic_camera && (
              <PanoramaViewer
                url={`${process.env.REACT_APP_CDN_URL}/${scanSessionFolder}/360/${scanSessionObject.scan_index}/`}
                sensorScanId={sensorScanId}
              />
            )}
          </div>)}
          {(currentUser?.has_pointcloud_access ?? true) && pointCloudCount && (<div className="viewer-pointcloud">
            {activeView === SensorType.lidar && (
              <PointCloudViewer
                url={`${process.env.REACT_APP_CDN_URL}/${scanSessionFolder}/pointclouds`}
                num_pc={pointCloudCount}
                ref={potreeChildRef}
              />
            )}
          </div>)}
        </>
      );
    } else {
      return (
        <Background>
          <div className="loader" style={{ top: "45%", left: "47%" }}>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </Background>
      );
    }
  }
}
